import { useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { Box, Drawer, Link, Stack } from '@mui/material';
// components
import { NAV } from '@raas/shared/src/shared-config-global';
import Logo from '@raas/shared/src/components/minimals/logo';
import Scrollbar from '@raas/shared/src/components/minimals/scrollbar';
import { NavSectionVertical } from '@raas/shared/src/components/minimals/nav-section';
// hooks
import SelectedLegalProvision from '@raas/shared/src/components/selected-legal-provision/SelectedLegalProvision';
import useResponsive from '@raas/shared/src/hooks/useResponsive';
// config
//
import LegalProvisionContext from '@raas/shared/src/contexts/LegalProvisionContext';
import { PATH_DASHBOARD } from '@raas/shared/src/routes/paths';
import NavToggleButton from './NavToggleButton';
import getNavConfig from './config-navigation';

// ----------------------------------------------------------------------

type Props = {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({ openNav, onCloseNav }: Props) {
  const { pathname } = useLocation();

  const { legalProvisionContext } = useContext(LegalProvisionContext);
  const hasTranslations = legalProvisionContext?.translations?.length > 1;
  const isDesktop = useResponsive('up', 'lg');

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo />
        <Link href={`${PATH_DASHBOARD.legalProvision.root}/${legalProvisionContext?.id}/details`}>
          <SelectedLegalProvision />
        </Link>
      </Stack>

      <NavSectionVertical
        data={getNavConfig({ hasVersion: !!legalProvisionContext, hasTranslations })}
      />
      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      <NavToggleButton />

      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              zIndex: 0,
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
